import { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Stack, useTheme, Typography } from "@mui/material";
import AtomAssistTypingIndicator from "src/reusable/Chat/ChatMessage/AtomAssistTypingIndicator";
import { AddCircle } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import axios from "axios";

import AtomAssistLogo from "src/views/assistant/components/AssistantLogo";
import AAAssistantText from "src/views/assistant/components/AAAssistantText";
import AAHeaderButton, {
  useIsHeaderCompact,
} from "src/views/assistant/components/header/AAHeaderButton";
import ChatContainer from "src/reusable/Chat/ChatContainer";
import { ChatWrapperContainer } from "src/reusable/Chat/StyledContainers/StyledContainers";
import { buildMessageTree } from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import { nuclearn_api_url } from "src/App";
import useLoadDialogue from "src/views/assistant/hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import {
  Dataset,
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";
import WorkflowChatInput from "src/views/products/workflow/components/WorkflowChatInput";
import { getMultipleDatasets } from "src/views/datasets/services/DatasetServices";
import { ITEM_EQUIVALENCY_PRODUCT } from "./itemEquivalencyProduct";
import { useItemEquivalencyChat } from "./hooks/useItemEquivalencyChat";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import ItemEquivalencySidebar from "./components/ItemEquivalencySidebar";
import { PersistedDialogueConfig } from "./services/ItemEquivalencyService";

/** The staged equivalency chat with its record-backed plan sidebar. */
const ItemEquivalencyChat = ({
  dialogueId,
  initialUserMessage,
}: {
  dialogueId: number | undefined;
  initialUserMessage?: string;
}) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useIsHeaderCompact();

  const {
    config: chatConfig,
    resetToBaseConfig,
    setActiveDialogueId,
    setDialogue: setStoreDialogue,
    setInitialUserMessage,
    setSourceDataRecordIds,
    setDropdownDatasets,
  } = useChatStore();

  const dropdownDatasetsLength = useChatStore(
    (state) => state.dropdownDatasets?.length ?? 0,
  );
  const activeDatasetIds = useActiveDatasetIds();
  const sourceDataRecordIds = useSourceDataRecordIds();

  const previousDialogueIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!dialogueId) return;
    setActiveDialogueId(dialogueId);
    if (
      previousDialogueIdRef.current !== undefined &&
      previousDialogueIdRef.current !== dialogueId
    ) {
      // Switching between two evaluations reuses this component, so clear
      // the previous one's documents — otherwise they ride the new run.
      // resetToBaseConfig already empties the dataset dropdown; documents
      // are the one piece it does not touch.
      setSourceDataRecordIds([]);
      resetToBaseConfig();
    }
    previousDialogueIdRef.current = dialogueId;
  }, [
    dialogueId,
    resetToBaseConfig,
    setActiveDialogueId,
    setSourceDataRecordIds,
  ]);

  const { dialogue: loadedDialogue } = useLoadDialogue({ initialUserMessage });

  // Hydrate once per dialogue so a later refetch cannot stomp a mid-session
  // attachment. Dialogues predating this config key hydrate to [], which is
  // inert only while the backend restores the stored scope for an omitted field.
  const hydratedDocumentScopeRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      !dialogueId ||
      loadedDialogue?.id !== dialogueId ||
      hydratedDocumentScopeRef.current === dialogueId
    ) {
      return;
    }
    const config = loadedDialogue.config as PersistedDialogueConfig | undefined;
    if (!config) return;
    hydratedDocumentScopeRef.current = dialogueId;
    setSourceDataRecordIds(config.source_data_record_ids ?? []);
  }, [dialogueId, loadedDialogue, setSourceDataRecordIds]);

  const persistedDatasetIds = useMemo(
    () =>
      (loadedDialogue?.config as PersistedDialogueConfig | undefined)
        ?.dataset_ids ?? [],
    [loadedDialogue],
  );

  // Fill the dataset dropdown from the ids stored with the evaluation.
  // setConfig seeds it with public datasets only, so a private reference
  // dataset would be dropped on reload and erased from the stored scope.
  useEffect(() => {
    if (persistedDatasetIds.length === 0) return;
    const currentIds = new Set(
      (useChatStore.getState().dropdownDatasets ?? []).map(
        (dataset) => dataset.id,
      ),
    );
    if (persistedDatasetIds.every((id: number) => currentIds.has(id))) return;
    let cancelled = false;
    getMultipleDatasets(persistedDatasetIds).then((datasets: Dataset[]) => {
      if (!cancelled && datasets?.length) setDropdownDatasets(datasets);
    });
    return () => {
      cancelled = true;
    };
  }, [persistedDatasetIds, dropdownDatasetsLength, setDropdownDatasets]);

  const dialogueTree = useMemo(() => {
    if (loadedDialogue?.messages && loadedDialogue.messages.length > 0) {
      return buildMessageTree(loadedDialogue.messages);
    }
    return [];
  }, [loadedDialogue]);

  const datasetsToUse = useMemo(
    () => activeDatasetIds.map((id) => ({ id })),
    [activeDatasetIds],
  );

  const chatConfigMemo = useMemo(() => {
    return {
      dialogueId,
      modelId: chatConfig?.modelId,
      modelVersionNum: chatConfig?.modelVersionNum,
      datasetsForSearchContext: datasetsToUse,
      sourceDataRecordIds:
        sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
      numSearchResults: chatConfig?.topSearchResults || 5,
      textGenModelParams: chatConfig?.chatControlMetadata,
      systemPrompt: chatConfig?.systemPrompt,
      toolCalling: true,
      chatConfigId: chatConfig?.chatConfigId,
      // Sent every turn — prefer the dialogue's own product (a legacy
      // dead-endpoint comparison row is still "parts-ai") over this page's.
      product: loadedDialogue?.product ?? ITEM_EQUIVALENCY_PRODUCT.productKey,
    };
  }, [
    dialogueId,
    loadedDialogue?.product,
    chatConfig?.modelId,
    chatConfig?.modelVersionNum,
    chatConfig?.chatControlMetadata,
    chatConfig?.systemPrompt,
    chatConfig?.chatConfigId,
    chatConfig?.topSearchResults,
    datasetsToUse,
    sourceDataRecordIds,
  ]);

  const {
    dialogue,
    artifacts,
    responseSources,
    setResponseSources,
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    isTurnActive,
    emptySubmission,
    clearChatErrors,
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    reset: resetChatStream,
    tokenUsage,
    setTokenUsage,
    retryConnection,
    lastDialogueId,
    activeSubAgents,
    connectToStream,
    store: chatStore,
  } = useItemEquivalencyChat(chatConfigMemo, dialogueTree);

  // Depend on the tree itself, not its length: gate answers are stamped onto
  // an EXISTING utterance, so a refetch can change content without changing
  // length. The tree is memoized on loadedDialogue, so the reference only
  // changes when the loaded data does.
  useEffect(() => {
    if (dialogueTree.length > 0) {
      setDialogue(dialogueTree);
      setStoreDialogue(dialogueTree);
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree,
    dialogueId,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  useStartChatOnMount({
    initialUserMessage,
    clearInitialMessage: () => setInitialUserMessage(undefined),
    emptySubmission,
    sendUserMessage,
  });

  // Resume a live parked session on page load (AtomAssistPage's probe,
  // simplified). Our gated flow parks the agent session at every
  // prompt_user_selection gate, so a reload or a dialogue opened from
  // recents MUST re-attach to the SSE stream — otherwise gate clicks are
  // delivered (chat_send) but the reprompt streams to nobody and the page
  // looks dead until refresh.
  const connectToStreamRef = useRef(connectToStream);
  connectToStreamRef.current = connectToStream;
  const isChatConnectedRef = useRef(isChatConnected);
  isChatConnectedRef.current = isChatConnected;
  useEffect(() => {
    if (!dialogueId || initialUserMessage) return;
    let cancelled = false;
    axios
      .get(`${nuclearn_api_url}/chat/session-active/${dialogueId}`)
      .then((resp) => {
        if (cancelled || !resp.data?.active) return;
        if (isChatConnectedRef.current) return;
        connectToStreamRef.current(dialogueId, { replay: true });
      })
      .catch(() => {
        // No live session — a finished dialogue loads from the DB as usual.
      });
    return () => {
      cancelled = true;
    };
  }, [dialogueId, initialUserMessage]);

  const onNewChat = useCallback(() => {
    useChatStore.getState().clearUploadedFiles();
    history.push(ITEM_EQUIVALENCY_PRODUCT.basePath);
  }, [history]);

  const LoadingIndicator = useMemo(
    () => () => <AtomAssistTypingIndicator showTimer />,
    [],
  );

  useEffect(() => {
    return () => {
      resetChatStream();
      terminateConnection();
      clearChatErrors();
    };
  }, [resetChatStream, terminateConnection, clearChatErrors]);

  return (
    <>
      <Box
        id="peq-chat-header"
        sx={{
          height: "55px",
          bgcolor: "background.paper",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {ITEM_EQUIVALENCY_PRODUCT.displayName}
        </Typography>
        <AAHeaderButton
          icon={<AddCircle />}
          label="New evaluation"
          onClick={onNewChat}
          tooltip="New evaluation"
          variant="contained"
          isMobile={isMobile}
          data-testid="peq-btn-new-chat"
          data-attr="assistant:btn:item-equivalency-new-chat"
        />
      </Box>
      <ChatStreamProvider store={chatStore}>
        <Stack
          direction="row"
          sx={{ height: "calc(100vh - 97px)", overflow: "hidden" }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <ChatWrapperContainer sx={{ flex: 1 }}>
              <ChatContainer
                activeSubAgents={activeSubAgents}
                dialogueId={dialogueId || undefined}
                dialogue={dialogue}
                isChatConnectionError={isChatConnectionError}
                chatStreamError={chatStreamError}
                isChatConnected={isChatConnected}
                isChatEnded={isChatEnded}
                isChatLoading={isChatLoading}
                isTurnActive={isTurnActive}
                isChatMessageStreamStopped={isChatMessageStreamStopped}
                startNewChat={onNewChat}
                resendUserMessage={resendUserMessage}
                regenerateResponse={regenerateResponse}
                setAsPreferredMessage={setAsPreferredMessage}
                onRetryConnection={lastDialogueId ? retryConnection : undefined}
                CustomLoadingIndicator={LoadingIndicator}
                ChatPromptInput={
                  <WorkflowChatInput
                    chatStreamError={chatStreamError}
                    terminateConnection={terminateConnection}
                    sendUserMessage={sendUserMessage}
                    isChatConnectionError={isChatConnectionError}
                    isChatLoading={isChatLoading}
                    isTurnActive={isTurnActive}
                    tokenUsage={tokenUsage}
                    product={ITEM_EQUIVALENCY_PRODUCT.productKey}
                    placeholder="Ask the Equivalency Evaluation Lead…"
                  />
                }
                BackgroundComponent={
                  <AtomAssistLogo
                    lowerMessage={
                      "How can I help with your equivalency evaluation?"
                    }
                  />
                }
                CustomAssistantText={AAAssistantText}
                CustomAssistantTextProps={{
                  isChatConnected,
                  responseSources,
                  onSourcesUpdate: setResponseSources,
                }}
              />
            </ChatWrapperContainer>
          </Box>

          <ItemEquivalencySidebar
            dialogueId={dialogueId}
            responseSources={responseSources}
            artifacts={artifacts}
          />
        </Stack>
      </ChatStreamProvider>
    </>
  );
};

export default ItemEquivalencyChat;
